import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MemberAccess } from 'app/trello/models/member-access';
import { ResourceNotFoundError } from 'app/trello/models/request-errors';
import { ApiTokenSender } from 'app/trello/services/api-token-sender';
import { MemberAccessFetcher } from 'app/trello/services/member-access-fetcher';
import { TokenService } from 'app/trello/services/token.service';
import { TrelloService } from 'app/trello/services/trello.service';
import { Templating } from 'app/trello/templating.imports';
import { environment } from 'environments/environment';
import { catchError, EMPTY, tap } from 'rxjs';

@Component({
  imports: [
    Templating,
  ],
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
})
export class OnboardingComponent implements OnInit {

  public authorizeButtonEnabled: boolean = true;
  public showingErrorView: boolean = false;
  public showingOnboardingView: boolean = false;
  public showingSynchronisingView: boolean = false;

  private readonly trelloOauthUrl: string =
    environment.trelloAuthorizeUrl +
    '?expiration=never' +
    `&name=${environment.appName}` +
    '&scope=read' +
    '&response_type=token' +
    `&key=${environment.appKey}` +
    `&return_url=${environment.webOrigin}${environment.paths.oauthCallback}`;

  private readonly waitForBoardSynchronisationDelay = 500;

  constructor(
    private readonly apiTokenSender: ApiTokenSender,
    private readonly memberAccessFetcher: MemberAccessFetcher,
    private readonly router: Router,
    private readonly tokenService: TokenService,
    private readonly trelloService: TrelloService, // required
  ) {
  }

  public ngOnInit(): void {
    this.fetchMemberAccessElseOnboard();
  }

  protected authenticateWithTrello(): void {

    const width = 640, height = 720;
    const left = (screen.width / 2) - (width / 2);
    const top = (screen.height / 2) - (height / 2);

    this.removeWindowEventListener();
    this.addWindowEventListener();

    window.open(
      this.trelloOauthUrl,
      'trello-auth',
      `width=${width},height=${height},top=${top},left=${left}`
    );
  }

  protected refreshApplication() {
    window.location.reload();
  }

  private fetchMemberAccessElseOnboard() {

    this.memberAccessFetcher.fetch()
      .pipe(
        tap((memberAccess) => {

          console.log('Member found');

          this.gotoHomeWhenBoardIsSynchronised(memberAccess);
        }),
        catchError((error) => {

          if (error instanceof ResourceNotFoundError) {

            console.warn('Member does not have access, onboarding ...');

            this.showOnboardingView();

          } else {

            console.error('An error occurred fetching member access:', error);

            this.showErrorView();
          }

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private saveTokens(
    apiToken: string,
  ): void {

    this.authorizeButtonEnabled = false;

    this.apiTokenSender.send(apiToken)
      .pipe(
        tap((memberAccess) => {

          console.log('Member onboarded');

          this.gotoHomeWhenBoardIsSynchronised(memberAccess);
        }),
        catchError((error) => {

          console.error('An error occurred whilst onboarding member:', error);

          this.showErrorView();

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private gotoHome() {

    this.router.navigate([environment.paths.home])
      .then(() => {
        // noop
      });
  }

  private gotoHomeWhenBoardIsSynchronised(
    memberAccess: MemberAccess,
  ) {

    if (memberAccess?.boardLastSynchronisedAt) {

      console.log(
        'Board synchronised to:',
        memberAccess.boardLastSynchronisedAt
      );

      this.tokenService.apiToken = memberAccess.apiToken;

      this.hideViews();
      this.gotoHome();

      return;
    }

    console.log('Board is not yet synchronised ...');

    this.showSynchronisingView();

    setTimeout(
      () => {

        this.memberAccessFetcher.fetch()
          .pipe(
            tap((memberAccess) => {
              this.gotoHomeWhenBoardIsSynchronised(memberAccess);
            }),
            catchError((error) => {

              this.showErrorView();

              console.error('An error occurred fetching member access:', error);

              return EMPTY;
            }),
          )
          .subscribe();
      },
      this.waitForBoardSynchronisationDelay,
    );
  }

  private addWindowEventListener(): void {

    window.addEventListener(
      'message',
      this.handleWindowMessage.bind(this),
      false,
    );
  }

  private removeWindowEventListener(): void {

    window.removeEventListener(
      'message',
      this.handleWindowMessage.bind(this),
      false,
    );
  }

  private handleWindowMessage(
    event: MessageEvent,
  ): void {

    if (!event.data || event.origin !== environment.webOrigin) {
      return;
    }

    const apiToken = event.data;

    this.saveTokens(apiToken);

    this.removeWindowEventListener();
  }

  private hideViews() {

    this.showingErrorView = false;
    this.showingOnboardingView = false;
    this.showingSynchronisingView = false;
  }

  private showErrorView() {

    this.hideViews();

    this.showingErrorView = true;
  }

  private showOnboardingView() {

    this.hideViews();

    this.showingOnboardingView = true;
  }

  private showSynchronisingView() {

    this.hideViews();

    this.showingSynchronisingView = true;
  }

}
